import { openNewChatComposer } from '@/lib/openHomeComposer';
import React, { useMemo, useCallback, useState } from 'react';
import { Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useRouter } from 'expo-router';
import { useCollectionStore, useAuthStore, useToastStore, useModalStore, useChatStore } from '@/store';
import { useChat } from '@/hooks/useChat';
import useRequireAuth from '@/hooks/useRequireAuth';
import Icon from '@/components/Icon';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';
import useResponsiveness from '@/hooks/useResponsiveness';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import { useTranslation } from '@/hooks/useTranslation';


function ChatDropdown({
  TriggerComponent,
  triggerAsChild = false,
  noRoot,
  withNewChat,
  conversationId,
  open,
  onOpenChange,
  isLongPress,
  withChatDetails,
  withWorkTasks = false,
  withSelectMultipleChats = false,
  side,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const requireAuth = useRequireAuth();

  const [isCollectionsSubOpen, setIsCollectionsSubOpen] = useState(false);
  const [isExportSubOpen, setIsExportSubOpen] = useState(false);

  const handleDropdownOpenChange = useCallback((open) => {
    if (!open) {
      setIsExportSubOpen(false);
      setIsCollectionsSubOpen(false);
    }
    onOpenChange?.(open);
  }, [onOpenChange]);

  // Multi-select state
  const isSelectingMultipleChats = useChatStore(state => state.isSelectingMultipleChats);
  const toggleSelectingMultipleChats = useChatStore(state => state.toggleSelectingMultipleChats);
  const startSelectingMultipleChats = useChatStore(state => state.startSelectingMultipleChats);
  const toggleSelectChatForMulti = useChatStore(state => state.toggleSelectChatForMulti);

  const handleStartSelecting = useCallback(() => {
    if (isSelectingMultipleChats) {
      toggleSelectingMultipleChats();
    } else {
      startSelectingMultipleChats();
      toggleSelectChatForMulti(conversationId);
    }
  }, [isSelectingMultipleChats, toggleSelectingMultipleChats, startSelectingMultipleChats, toggleSelectChatForMulti, conversationId]);

  // Get the raw collections data
  const collectionsMap = useCollectionStore(state => state.collections);
  const collectionIds = useCollectionStore(state => state.collectionIds);
  const addConversationToCollection = useCollectionStore(state => state.addConversationToCollection);
  const removeConversationFromCollection = useCollectionStore(state => state.removeConversationFromCollection);

  // Memoize the collections array
  const collections = useMemo(() => {
    return collectionIds.map((id) => collectionsMap[id]);
  }, [collectionsMap, collectionIds]);

  const handleCreateNewCollection = useCallback(() => {
    requireAuth(
      () => openModal('CreateCollectionModal'),
      {
        source: 'chat_dropdown_create_collection',
        title: 'Sign in to create a Collection',
        subtitle: 'Collections group files and chats so Gab can pull from the same context across every conversation.',
        icon: 'folder-plus',
      },
    );
  }, [openModal, requireAuth]);

  // Destructure all needed values from useChat hook
  const { 
    chat,
    isTemporary,
    isPinned, 
    isArchiving, 
    isDeleting, 
    isArchived,
    handleArchiveConfirm, 
    handleDeleteConfirm, 
    handleShare, 
    handleTogglePinChat, 
    handleRename,
    handleExportChat,
    handleSaveTemporaryConfirm,
    handleOpenTemporarySettings,
  } = useChat(conversationId);

  const inCollectionId = useMemo(() => chat?.inCollectionId, [chat?.inCollectionId]);
  const canManageWorkTasks = withWorkTasks && chat?.isOwner && !isTemporary && !isGhost;
  const handleManageWorkTasks = useCallback(() => {
    openModal('WorkChatTasksModal', { conversationId, chatTitle: chat?.title });
  }, [openModal, conversationId, chat?.title]);

  const handleReport = useCallback(() => {
    openModal('ReportModal', {
      isOpen: true,
      conversationId,
    });
  }, [openModal, conversationId]);

  const handleAddToCollection = useCallback(async (collectionId) => {
    try {
      if (collectionId === null && inCollectionId) {
        await removeConversationFromCollection(inCollectionId, conversationId);
        addToast(t('dropdowns.chatRemovedFromCollection'), 'success');
      } else {
        await addConversationToCollection(collectionId, conversationId);
        addToast(t('dropdowns.chatAddedToCollection'), 'success', [
          {
            title: t('dropdowns.viewCollection'),
            href: `/collections/${collectionId}`,
          }
        ]);
      }
    } catch (error) {
      console.error('Error managing chat collection:', error);
    }
  }, [conversationId, inCollectionId, addConversationToCollection, removeConversationFromCollection, addToast]);

  const content = (
    <>
      {TriggerComponent && (
        <DropdownMenu.Trigger asChild={triggerAsChild} action={isLongPress ? 'longPress' : undefined}>
          <TriggerComponent />
        </DropdownMenu.Trigger>
      )}

      <DropdownMenu.Content 
        className={Platform.OS === 'web' ? "DropdownMenuContent" : undefined}
        side={side}
        align={isXS ? 'center' : 'end'} 
        sideOffset={isXS ? 4 : 5}
      >
        {Platform.OS === 'web' ? (
          // Web implementation
          <>
            {isXS && isExportSubOpen ? (
              // Mobile inline export sub-view
              <>
                <div
                  key="export-back"
                  className="DropdownMenuItem"
                  onClick={() => setIsExportSubOpen(false)}
                >
                  <div className="DropdownMenuItemIcon">
                    <Icon name="chevron-left" size={12} color={colors.text_color_primary} />
                  </div>
                  <span className="DropdownMenuItemTitle">
                    {t('dropdowns.back')}
                  </span>
                </div>
                <DropdownMenuItemDivider key="export-back-divider" />
                <DropdownMenuItem
                  key="export-pdf"
                  onSelect={() => handleExportChat('pdf')}
                  icon="file-pdf"
                  title={t('dropdowns.exportAsPdf')}
                />
                <DropdownMenuItem
                  key="export-markdown"
                  onSelect={() => handleExportChat('markdown')}
                  icon="file-lines"
                  title={t('dropdowns.exportAsMarkdown')}
                />
                <DropdownMenuItem
                  key="export-text"
                  onSelect={() => handleExportChat('text')}
                  icon="file"
                  title={t('dropdowns.exportAsText')}
                />
              </>
            ) : isXS && isCollectionsSubOpen ? (
              // Mobile inline collections sub-view
              <>
                <div
                  key="collection-back"
                  className="DropdownMenuItem"
                  onClick={() => setIsCollectionsSubOpen(false)}
                >
                  <div className="DropdownMenuItemIcon">
                    <Icon name="chevron-left" size={12} color={colors.text_color_primary} />
                  </div>
                  <span className="DropdownMenuItemTitle">
                    {t('dropdowns.back')}
                  </span>
                </div>
                <DropdownMenuItemDivider key="collection-back-divider" />
                {Array.isArray(collections) && collections?.filter(collection => collection.id !== inCollectionId).map(collection => (
                  <DropdownMenuItem
                    key={`collection-${collection.id}`}
                    onSelect={() => handleAddToCollection(collection.id)}
                    icon="folder"
                    title={collection.title}
                  />
                ))}
                {(Array.isArray(collections) && collections?.length === 0 || (inCollectionId && collections.length === 1)) && (
                  <>
                    <DropdownMenuItem
                      key="new-collection"
                      icon="folder-plus"
                      title={t('dropdowns.newCollection')}
                      onSelect={handleCreateNewCollection}
                    />
                    <DropdownMenuItemDivider key="collection-divider" />
                    <DropdownMenuItem
                      key="no-collections"
                      icon="folder"
                      title={t('dropdowns.noCollections')}
                      disabled
                    />
                  </>
                )}
              </>
            ) : (
              // Main menu (always on desktop, default on mobile)
              <>
                {(chat?.title || isTemporary) && (
                  <>
                    <DropdownMenuLabel key="chat-title" text={isTemporary ? t('home.temporaryChat') : chat.title} />
                    <DropdownMenuItemDivider key="chat-title-divider" />
                  </>
                )}
                {isTemporary && (
                  <>
                    <DropdownMenuItem
                      key="save-temporary"
                      onSelect={handleSaveTemporaryConfirm}
                      icon="bookmark"
                      title={t('dropdowns.saveThisChat')}
                    />
                    <DropdownMenuItem
                      key="temporary-settings"
                      onSelect={handleOpenTemporarySettings}
                      icon="sliders"
                      title={t('dropdowns.temporaryChatSettings')}
                    />
                    <DropdownMenuItemDivider key="temporary-divider" />
                  </>
                )}
                {withNewChat && (
                  <DropdownMenuItem
                    key="new-chat"
                    onSelect={() => openNewChatComposer(router)}
                    icon="pen-to-square"
                    title={t('dropdowns.newChat')}
                  />
                )}
                {(withSelectMultipleChats && !inCollectionId && !isTemporary) && (
                  <DropdownMenuItem
                    key="select-multiple"
                    onSelect={handleStartSelecting}
                    icon="square-check"
                    title={isSelectingMultipleChats ? t('dropdowns.doneSelecting') : t('dropdowns.select')}
                  />
                )}
                {!isGhost && !isTemporary && (
                  <DropdownMenuItem
                    key="pin"
                    onSelect={handleTogglePinChat}
                    icon="star"
                    title={isPinned ? t('dropdowns.unpin') : t('dropdowns.pin')}
                  />
                )}
                {!isTemporary && (
                  <DropdownMenuItem
                    key="rename"
                    onSelect={handleRename}
                    icon="pencil"
                    title={t('dropdowns.rename')}
                  />
                )}
                {canManageWorkTasks && (
                  <DropdownMenuItem
                    key="work-tasks"
                    onSelect={handleManageWorkTasks}
                    icon="calendar-check"
                    title={t('tasks.manageWorkChatTasks')}
                  />
                )}
                {withChatDetails && (
                  <DropdownMenu.Item
                    key="chat-details"
                    className="DropdownMenuItem"
                    onSelect={() => openModal('ChatDetailsModal', { conversationId })}
                  >
                    <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                      <Icon name="table-columns" size={14} color={colors.text_color_primary} />
                    </DropdownMenu.ItemIcon>
                    <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                      {t('dropdowns.chatDetails')}
                    </DropdownMenu.ItemTitle>
                    <DropdownMenu.ItemIcon className="DropdownMenuItemIcon RightSlot">
                      <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                    </DropdownMenu.ItemIcon>
                  </DropdownMenu.Item>
                )}
                {!isGhost && !isTemporary && (
                  <>
                    <DropdownMenuItem
                      key="share"
                      onSelect={handleShare}
                      icon="share"
                      title={t('dropdowns.share')}
                    />
                    {isXS ? (
                      <div
                        key="export-trigger"
                        className="DropdownMenuItem"
                        onClick={() => setIsExportSubOpen(true)}
                      >
                        <div className="DropdownMenuItemIcon">
                          <Icon name="file-export" size={14} color={colors.text_color_primary} />
                        </div>
                        <span className="DropdownMenuItemTitle">
                          {t('dropdowns.exportChat')}
                        </span>
                        <div className="DropdownMenuItemIcon RightSlot">
                          <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                        </div>
                      </div>
                    ) : (
                      <DropdownMenu.Sub
                        open={isExportSubOpen}
                        onOpenChange={setIsExportSubOpen}
                      >
                        <DropdownMenu.SubTrigger className="DropdownMenuItem" key="export-trigger">
                          <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                            <Icon name="file-export" size={14} color={colors.text_color_primary} />
                          </DropdownMenu.ItemIcon>
                          <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                            {t('dropdowns.exportChat')}
                          </DropdownMenu.ItemTitle>
                          <DropdownMenu.ItemIcon className="DropdownMenuItemIcon RightSlot">
                            <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                          </DropdownMenu.ItemIcon>
                        </DropdownMenu.SubTrigger>

                        <DropdownMenu.SubContent
                          className="DropdownMenuSubContent"
                          side="right"
                          align="start"
                          sideOffset={4}
                          avoidCollisions={true}
                          collisionPadding={16}
                        >
                          <DropdownMenuItem
                            key="export-pdf"
                            onSelect={() => handleExportChat('pdf')}
                            icon="file-pdf"
                            title={t('dropdowns.exportAsPdf')}
                          />
                          <DropdownMenuItem
                            key="export-markdown"
                            onSelect={() => handleExportChat('markdown')}
                            icon="file-lines"
                            title={t('dropdowns.exportAsMarkdown')}
                          />
                          <DropdownMenuItem
                            key="export-text"
                            onSelect={() => handleExportChat('text')}
                            icon="file"
                            title={t('dropdowns.exportAsText')}
                          />
                        </DropdownMenu.SubContent>
                      </DropdownMenu.Sub>
                    )}
                    {isXS ? (
                      <div
                        key="collection-trigger"
                        className="DropdownMenuItem"
                        onClick={() => setIsCollectionsSubOpen(true)}
                      >
                        <div className="DropdownMenuItemIcon">
                          <Icon name="folder-plus" size={14} color={colors.text_color_primary} />
                        </div>
                        <span className="DropdownMenuItemTitle">
                          {inCollectionId ? t('dropdowns.moveToCollection') : t('dropdowns.addToCollection')}
                        </span>
                        <div className="DropdownMenuItemIcon RightSlot">
                          <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                        </div>
                      </div>
                    ) : (
                      <DropdownMenu.Sub
                        open={isCollectionsSubOpen}
                        onOpenChange={setIsCollectionsSubOpen}
                      >
                        <DropdownMenu.SubTrigger className="DropdownMenuItem" key="collection-trigger">
                          <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                            <Icon name="folder-plus" size={14} color={colors.text_color_primary} />
                          </DropdownMenu.ItemIcon>
                          <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                            {inCollectionId ? t('dropdowns.moveToCollection') : t('dropdowns.addToCollection')}
                          </DropdownMenu.ItemTitle>
                          <DropdownMenu.ItemIcon className="DropdownMenuItemIcon RightSlot">
                            <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                          </DropdownMenu.ItemIcon>
                        </DropdownMenu.SubTrigger>

                        <DropdownMenu.SubContent
                          className="DropdownMenuSubContent"
                          side="right"
                          align="start"
                          sideOffset={4}
                          avoidCollisions={true}
                          collisionPadding={16}
                        >
                          {Array.isArray(collections) && collections?.filter(collection => collection.id !== inCollectionId).map(collection => (
                            <DropdownMenuItem
                              key={`collection-${collection.id}`}
                              onSelect={() => handleAddToCollection(collection.id)}
                              icon="folder"
                              title={collection.title}
                            />
                          ))}
                          {(Array.isArray(collections) && collections?.length === 0 || (inCollectionId && collections.length === 1)) && (
                            <>
                              <DropdownMenuItem
                                key="new-collection"
                                icon="folder-plus"
                                title={t('dropdowns.newCollection')}
                                onSelect={handleCreateNewCollection}
                              />
                              <DropdownMenuItemDivider key="collection-divider" />
                              <DropdownMenuItem
                                key="no-collections"
                                icon="folder"
                                title={t('dropdowns.noCollections')}
                                disabled
                              />
                            </>
                          )}
                        </DropdownMenu.SubContent>
                      </DropdownMenu.Sub>
                    )}
                    
                    {inCollectionId && !isTemporary && (
                      <DropdownMenuItem
                        key="remove-from-collection"
                        onSelect={() => handleAddToCollection(null)}
                        icon="folder-minus"
                        title={t('dropdowns.removeFrom').replace('%NAME%', collections.find(c => c.id === inCollectionId)?.title || t('dropdowns.collection'))}
                      />
                    )}
                    
                    {!isTemporary && (
                      <DropdownMenuItem
                        key="archive"
                        onSelect={handleArchiveConfirm}
                        icon="box-archive"
                        title={isArchived ? t('dropdowns.unarchive') : t('dropdowns.archive')}
                        isLoading={isArchiving}
                      />
                    )}
                  </>
                )}

                <DropdownMenuItem
                  key="report"
                  onSelect={handleReport}
                  icon="flag"
                  title={t('dropdowns.report')}
                />

                <DropdownMenuItem
                  key="delete"
                  onSelect={handleDeleteConfirm}
                  icon="trash"
                  title={t('common.delete')}
                  isDanger={true}
                  isLoading={isDeleting}
                />
              </>
            )}
          </>
        ) : (
          // Native implementation
          <>
            {(chat?.title || isTemporary) && (
              <>
                <DropdownMenu.Item key="chat-title" disabled>
                  <DropdownMenu.ItemTitle numberOfLines={1}>
                    {isTemporary ? t('home.temporaryChat') : chat.title}
                  </DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
              </>
            )}
            {isTemporary && (
              <>
                <DropdownMenu.Item key="save-temporary" onSelect={handleSaveTemporaryConfirm}>
                  <DropdownMenu.ItemIcon ios={{
                    name: "bookmark",
                    color: colors.text_color_primary
                  }} />
                  <DropdownMenu.ItemTitle>{t('dropdowns.saveThisChat')}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
                <DropdownMenu.Item key="temporary-settings" onSelect={handleOpenTemporarySettings}>
                  <DropdownMenu.ItemIcon ios={{
                    name: "slider.horizontal.3",
                    color: colors.text_color_primary
                  }} />
                  <DropdownMenu.ItemTitle>{t('dropdowns.temporaryChatSettings')}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
              </>
            )}
            {withNewChat && (
              <DropdownMenu.Item key="new-chat" onSelect={() => openNewChatComposer(router)}>
                <DropdownMenu.ItemIcon ios={{ 
                  name: "square.and.pencil",
                  color: colors.text_color_primary 
                }} />
                <DropdownMenu.ItemTitle>{t('dropdowns.newChat')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {withSelectMultipleChats && !inCollectionId && !isTemporary && (
              <DropdownMenu.Item key="select-multiple" onSelect={handleStartSelecting}>
                <DropdownMenu.ItemIcon ios={{ 
                  name: "checkmark.circle",
                  color: colors.text_color_primary 
                }} />
                <DropdownMenu.ItemTitle>
                  {isSelectingMultipleChats ? t('dropdowns.doneSelecting') : t('dropdowns.select')}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {!isGhost && !isTemporary && (
              <DropdownMenu.Item key="pin" onSelect={handleTogglePinChat}>
                <DropdownMenu.ItemIcon ios={{ 
                  name: "star",
                  color: colors.text_color_primary 
                }} />
                <DropdownMenu.ItemTitle>{isPinned ? t('dropdowns.unpin') : t('dropdowns.pin')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {!isTemporary && (
              <DropdownMenu.Item key="rename" onSelect={handleRename}>
                <DropdownMenu.ItemIcon ios={{ 
                  name: "pencil",
                  color: colors.text_color_primary 
                }} />
                <DropdownMenu.ItemTitle>{t('dropdowns.rename')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {canManageWorkTasks && (
              <DropdownMenu.Item key="work-tasks" onSelect={handleManageWorkTasks}>
                <DropdownMenu.ItemIcon ios={{ name: 'calendar', color: colors.text_color_primary }} />
                <DropdownMenu.ItemTitle>{t('tasks.manageWorkChatTasks')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {withChatDetails && !isTemporary && (
              <DropdownMenu.Item key="chat-details" onSelect={() => openModal('ChatDetailsModal', { conversationId })}>
                <DropdownMenu.ItemIcon ios={{ 
                  name: "sidebar.right",
                  color: colors.text_color_primary 
                }} />
                <DropdownMenu.ItemTitle>{t('dropdowns.chatDetails')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {!isGhost && !isTemporary && (
              <>
                <DropdownMenu.Item key="share" onSelect={handleShare}>
                  <DropdownMenu.ItemIcon ios={{ 
                    name: "square.and.arrow.up",
                    color: colors.text_color_primary 
                  }} />
                  <DropdownMenu.ItemTitle>{t('dropdowns.share')}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>

                <DropdownMenu.Sub
                  open={isExportSubOpen}
                  onOpenChange={setIsExportSubOpen}
                >
                  <DropdownMenu.SubTrigger key="export-trigger">
                    <DropdownMenu.ItemIcon ios={{ 
                      name: "square.and.arrow.down",
                      color: colors.text_color_primary 
                    }} />
                    <DropdownMenu.ItemTitle>{t('dropdowns.exportChat')}</DropdownMenu.ItemTitle>
                  </DropdownMenu.SubTrigger>

                  <DropdownMenu.SubContent
                    side={isXS ? 'bottom' : 'right'}
                    align={isXS ? 'center' : 'start'}
                    sideOffset={isXS ? 6 : 4}
                  >
                    <DropdownMenu.Item key="export-pdf" onSelect={() => handleExportChat('pdf')}>
                      <DropdownMenu.ItemIcon ios={{
                        name: "doc.fill",
                        color: colors.text_color_primary
                      }} />
                      <DropdownMenu.ItemTitle>{t('dropdowns.exportAsPdf')}</DropdownMenu.ItemTitle>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item key="export-markdown" onSelect={() => handleExportChat('markdown')}>
                      <DropdownMenu.ItemIcon ios={{ 
                        name: "doc.richtext",
                        color: colors.text_color_primary 
                      }} />
                      <DropdownMenu.ItemTitle>{t('dropdowns.exportAsMarkdown')}</DropdownMenu.ItemTitle>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item key="export-text" onSelect={() => handleExportChat('text')}>
                      <DropdownMenu.ItemIcon ios={{ 
                        name: "doc.text",
                        color: colors.text_color_primary 
                      }} />
                      <DropdownMenu.ItemTitle>{t('dropdowns.exportAsText')}</DropdownMenu.ItemTitle>
                    </DropdownMenu.Item>
                  </DropdownMenu.SubContent>
                </DropdownMenu.Sub>

                <DropdownMenu.Sub
                  open={isCollectionsSubOpen}
                  onOpenChange={setIsCollectionsSubOpen}
                >
                  <DropdownMenu.SubTrigger key="collection-trigger">
                    <DropdownMenu.ItemIcon ios={{ 
                      name: "folder.badge.plus",
                      color: colors.text_color_primary 
                    }} />
                    <DropdownMenu.ItemTitle>
                      {inCollectionId ? t('dropdowns.moveToCollection') : t('dropdowns.addToCollection')}
                    </DropdownMenu.ItemTitle>
                  </DropdownMenu.SubTrigger>

                  <DropdownMenu.SubContent
                    side={isXS ? 'bottom' : 'right'}
                    align={isXS ? 'center' : 'start'}
                    sideOffset={isXS ? 6 : 4}
                  >
                    {isXS && (
                      <>
                        <DropdownMenu.Item
                          key="collection-back"
                          onSelect={() => setIsCollectionsSubOpen(false)}
                        >
                          <DropdownMenu.ItemIcon ios={{ 
                            name: "chevron.backward",
                            color: colors.text_color_primary 
                          }} />
                          <DropdownMenu.ItemTitle>{t('dropdowns.back')}</DropdownMenu.ItemTitle>
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator />
                      </>
                    )}
                    {Array.isArray(collections) && collections?.filter(collection => collection.id !== inCollectionId).map(collection => (
                      <DropdownMenu.Item 
                        key={`collection-${collection.id}`}
                        onSelect={() => handleAddToCollection(collection.id)}
                      >
                        <DropdownMenu.ItemIcon ios={{ 
                          name: "folder",
                          color: colors.text_color_primary 
                        }} />
                        <DropdownMenu.ItemTitle>{collection.title}</DropdownMenu.ItemTitle>
                      </DropdownMenu.Item>
                    ))}
                    {(Array.isArray(collections) && collections?.length === 0 || (inCollectionId && collections.length === 1)) && (
                      <>
                        <DropdownMenu.Item key="new-collection" onSelect={handleCreateNewCollection}>
                          <DropdownMenu.ItemIcon ios={{ 
                            name: "folder.badge.plus",
                            color: colors.text_color_primary 
                          }} />
                          <DropdownMenu.ItemTitle>{t('dropdowns.newCollection')}</DropdownMenu.ItemTitle>
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator key="collection-divider" />
                        <DropdownMenu.Item key="no-collections" disabled>
                          <DropdownMenu.ItemIcon ios={{ 
                            name: "folder",
                            color: colors.text_color_secondary 
                          }} />
                          <DropdownMenu.ItemTitle>{t('dropdowns.noCollections')}</DropdownMenu.ItemTitle>
                        </DropdownMenu.Item>
                      </>
                    )}
                  </DropdownMenu.SubContent>
                </DropdownMenu.Sub>

                {inCollectionId && (
                  <DropdownMenu.Item key="remove-from-collection" onSelect={() => handleAddToCollection(null)}>
                    <DropdownMenu.ItemIcon ios={{ 
                      name: "folder.badge.minus",
                      color: colors.text_color_primary 
                    }} />
                    <DropdownMenu.ItemTitle>
                      {t('dropdowns.removeFrom').replace('%NAME%', collections.find(c => c.id === inCollectionId)?.title || t('dropdowns.collection'))}
                    </DropdownMenu.ItemTitle>
                  </DropdownMenu.Item>
                )}

                <DropdownMenu.Item key="archive" onSelect={handleArchiveConfirm}>
                  <DropdownMenu.ItemIcon ios={{ 
                    name: "archivebox",
                    color: colors.text_color_primary 
                  }} />
                  <DropdownMenu.ItemTitle>
                    {isArchived ? t('dropdowns.unarchive') : t('dropdowns.archive')}
                  </DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
              </>
            )}
            {!isGhost && (
              <>
                <DropdownMenu.Item key="report" onSelect={handleReport}>
                  <DropdownMenu.ItemIcon ios={{ 
                    name: "flag",
                    color: colors.text_color_primary 
                  }} />
                  <DropdownMenu.ItemTitle>{t('dropdowns.report')}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>

                <DropdownMenu.Item key="delete" destructive onSelect={handleDeleteConfirm}>
                  <DropdownMenu.ItemIcon ios={{ name: "trash" }} />
                  <DropdownMenu.ItemTitle>
                    {t('common.delete')}
                  </DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
              </>
            )}
          </>
        )}
      </DropdownMenu.Content>
    </>
  );

  if (noRoot) {
    return content;
  }

  return (
    <DropdownMenu.Root
      {...(open !== undefined ? { open } : {})}
      onOpenChange={handleDropdownOpenChange}
    >
      {content}
    </DropdownMenu.Root>
  );
}

export default React.memo(ChatDropdown);
